import { useField, useFormikContext } from 'formik';
import * as React from 'react';
import { ReactElement, useEffect, useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'react-use';
import { Box, Flex } from 'reflexbox';
import styled, { css } from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import { CloseIcon, UploadIcon } from '@treams/treams-icons';
import { GroupType } from '@treams/treams-network';
import { LocalizedString } from '@treams/treams-network';
import {
	Button,
	Combo,
	Field,
	FieldType,
	GraphQLErrorMessage,
	IconButton,
	ImageUpload,
	Input,
	InputElement,
	MultiSelect,
	Paragraph,
	QuillInput,
	Select,
	Spinner,
	Switch,
	tokens,
} from '@treams/treams-ui';

import GroupSelector from '../../../../../goals/group-selector/GroupSelector';
import { MutationHook, QueryHook } from '../../../../../settings/generic/types';

const Wrapper = styled.div`
	width: 100%;

	input {
		width: 100%;
	}
`;

export const Value = styled(Paragraph)`
	display: flex;
	align-items: center;
	padding: 8px 19px;
	line-height: 30px;
	margin: 0;
	font-size: 16px;
	cursor: pointer;
	border: 1px solid ${(props) => props.theme.colors.lightBlueDark};
	transition: 150ms ease-out border-color;
	border-radius: 4px;

	&:hover {
		border-color: ${(props) => props.theme.colors.lightBlueLight};
	}
`;

type ValueWrapperProps = {
	focused?: boolean;
};

const ValueWrapper = styled.div<ValueWrapperProps>`
	// Display none for all Field values

	> div,
	> input {
		display: none;
	}

	span {
		display: block;
	}

	${(props) =>
		props.focused &&
		css`
			> div,
			> input {
				display: block;
			}

			span {
				display: none;
			}
		`}
`;

export enum DetailsTableFormValueTypes {
	Date,
	Text,
	TextMultiline,
	Custom,
	Multilingual,
	MultiSelect,
	MultilingualQuill,
	Number,
	Select,
	Switch,
	ColorPicker,
	CreateSelect,
	GroupSelector,
	ImageUpload,
	Quill,
}

interface NumberProps {
	initialValue?: number;
	name: string;
	label?: string;
	onEditingForm?: (isEditing: boolean) => void;
	onChange?: (numberValue?: number) => void;
	type: DetailsTableFormValueTypes;
	alwaysFocused?: boolean;
}

function NumberValue({
	initialValue,
	onEditingForm = () => {},
	name,
	label,
	onChange = () => {},
	type,
	alwaysFocused = false,
}: NumberProps) {
	const [isFocused, setIsFocused] = useState(false);
	const [numberValue, setNumberValue] = useState(initialValue);
	const formik = useFormikContext();

	useEffect(() => onChange(numberValue), [numberValue]);

	function handleClick() {
		setIsFocused(true);
		// TODO: Handle state to SettingsCreateNewTeam to display that you're edting the form.
		// onEditingForm(true);
	}

	function toNumberValue(value: string): number | undefined {
		if (value !== '') {
			return parseInt(value, 10);
		}
		return undefined;
	}

	function onBlur(event?: { target: { value: string } }) {
		setIsFocused(false);

		if (event) {
			setNumberValue(toNumberValue(event.target.value));
		}
	}

	return (
		<ValueWrapper focused={isFocused || alwaysFocused}>
			<span
				onClick={handleClick}
				onKeyUp={handleClick}
				role="button"
				tabIndex={0}
				aria-label={label}
			>
				<Value color="slateBlue">
					{numberValue !== null && numberValue !== undefined && typeof numberValue === 'number'
						? numberValue.toFixed()
						: '—'}
				</Value>
			</span>

			{(isFocused || alwaysFocused) && (
				<Field
					autoFocus
					type={FieldType.Number}
					placeholder="—"
					name={name}
					aria-label={label}
					onBlur={onBlur}
					onChange={(event) =>
						formik.handleChange(event.target.name)({
							target: {
								value: toNumberValue(event.target.value),
							},
						} as any)
					}
					fullWidth
					resizable={type === DetailsTableFormValueTypes.TextMultiline}
					unspaced
					hideError
				/>
			)}
		</ValueWrapper>
	);
}

interface TextProps {
	initialValue?: string;
	name: string;
	label?: string;
	onEditingForm?: (isEditing: boolean) => void;
	onChange?: (textValue?: string) => void;
	type: DetailsTableFormValueTypes;
	alwaysFocused?: boolean;
}

function TextValue({
	initialValue,
	onEditingForm = () => {},
	name,
	label,
	onChange = () => {},
	type,
	alwaysFocused = false,
}: TextProps) {
	const [isFocused, setIsFocused] = useState(false);
	const [textValue, setTextValue] = useState(initialValue);

	useEffect(() => onChange(textValue), [textValue]);

	function handleClick() {
		setIsFocused(true);
		// TODO: Handle state to SettingsCreateNewTeam to display that you're edting the form.
		// onEditingForm(true);
	}

	function onBlur(event?: React.ChangeEvent<HTMLInputElement>) {
		setIsFocused(false);
		// TODO: Handle state to SettingsCreateNewTeam to display that you're edting the form.
		// onEditingForm(false);

		if (event) {
			if (event.target.value !== '') {
				setTextValue(event.target.value);
			} else {
				setTextValue('');
			}
		}
	}

	return (
		<ValueWrapper focused={isFocused || alwaysFocused}>
			<span
				onClick={handleClick}
				onKeyUp={handleClick}
				role="button"
				tabIndex={0}
				aria-label={label}
			>
				<Value color="slateBlue">{textValue || '—'}</Value>
			</span>

			{(isFocused || alwaysFocused) && (
				<Field
					autoFocus
					type={FieldType.Text}
					placeholder="—"
					name={name}
					aria-label={label}
					onBlur={onBlur}
					fullWidth
					resizable={type === DetailsTableFormValueTypes.TextMultiline}
					unspaced
					hideError
				/>
			)}
		</ValueWrapper>
	);
}

function DateValue({ name }: { name: string }) {
	const [field, , { setValue }] = useField<Date | string | undefined>(name);

	return (
		<Flex alignItems="center">
			<Box flexGrow={1}>
				<Input.DatePicker.Formik name={name} />
			</Box>
			{field.value && (
				<Box ml={2}>
					<IconButton
						icon={<CloseIcon />}
						type="secondary"
						onClick={() => {
							setValue(undefined);
						}}
					/>
				</Box>
			)}
		</Flex>
	);
}

export interface IOptions {
	label: string;
	value: string;
	color?: string;
}

interface IOvalProps {
	color?: string;
}

const Oval = styled.div<IOvalProps>`
	background-color: ${({ color }) => color};
	border-radius: 50%;
	height: 12px;
	width: 12px;
	margin: 0 8px 0 0;
`;

interface ISelectProps {
	options?: IOptions[];
	name: string;
	label?: string;
	search?: boolean;
}

interface ICreateSelectProps {
	fetchContent?: QueryHook;
	createContent?: MutationHook;
	name: string;
	placeholder?: string;
}

const ClearButton = styled.div`
	color: ${(props) => props.theme.colors.primary};
	background: rgba(0, 0, 0, 0.1);
	border-radius: 12px;
	width: 24px;
	height: 24px;
	display: flex;
	align-items: center;
	justify-content: center;

	svg {
		display: block;

		* {
			fill: ${tokens.text.secondary};
		}
	}
`;

function SelectValue({ options, name, label, search = false }: ISelectProps) {
	const [field, , { setValue }] = useField<string | undefined>(name);

	return (
		<>
			<Select
				role={label ? 'combobox' : undefined}
				aria-label={label}
				options={options?.map((option) => ({
					title: option.label,
					value: option.value,
				}))}
				onChange={(value) => {
					setValue(value);
				}}
				selectedValue={field.value}
				search={search}
			/>
		</>
	);
}

function CreateSelectValue({ fetchContent, createContent, name, placeholder }: ICreateSelectProps) {
	const { localize } = useLocalize();
	const [field, , { setValue }] = useField<string | undefined>(name);

	if (!fetchContent || !createContent) {
		return 'no fetchContent';
	}

	const { data, loading, error } = fetchContent();

	const [create] = createContent();

	const options =
		data &&
		data.noteTemplateCategories.map((item) => ({
			title: localize(item.title as LocalizedString).text,
			value: item.id,
		}));

	const preSelectedValue = options?.find((option) => option.value === field.value)?.title;

	const updateItem = (choice: { custom: string } | { value: string } | undefined) => {
		if (choice && 'value' in choice) {
			return setValue(choice.value);
		}

		if (choice && 'custom' in choice) {
			return submitItem(choice.custom);
		}
	};

	const submitItem = async (value: string) => {
		const item = await create({
			variables: {
				input: {
					title: [
						{
							languageID: localStorage.getItem('language') ?? 'nl',
							text: value,
						},
					],
				},
			},
		});
		await setValue(item.data?.createNoteTemplateCategory.id);
	};

	if (loading) {
		return <Spinner centered />;
	}

	return (
		<>
			<Combo
				preSelectedValue={preSelectedValue}
				placeholder={placeholder}
				onChange={(choice) => updateItem(choice)}
				options={options}
			/>

			{error && <GraphQLErrorMessage error={error.graphQLErrors[0]} />}
		</>
	);
}

function MultiSelectValue({ options, name, label }: ISelectProps) {
	const [field, , { setValue }] = useField<string[] | undefined>(name);
	const selected = options?.filter((option) => field.value?.includes(option.value));

	return (
		<MultiSelect
			aria-label={label}
			options={options?.map((option) => ({
				title: option.label,
				value: option.value,
			}))}
			onChange={(value) => {
				setValue(value);
			}}
			selectedOptions={
				selected?.map((option) => ({
					title: option.label,
					value: option.value,
				})) ?? []
			}
		/>
	);
}

interface IGroupSelectorProps {
	name: string;
	types?: GroupType[];
	single?: boolean;
	required?: boolean;
	disabled?: boolean;
}

function GroupSelectorValue({ name, types, single, required, disabled }: IGroupSelectorProps) {
	return (
		<GroupSelector.Formik
			name={name}
			single={single}
			required={required}
			disabled={disabled}
			types={types}
			canSelect={() => true}
		/>
	);
}

interface IImageUploadProps {
	name: string;
	accept?: string[];
	onUpload?: (file: File) => Promise<{ id: string; url: string }>;
	uploading?: boolean;
}

const ImagePreviewContainer = styled(Box)`
	position: relative;
	border-radius: 8px;
	overflow: hidden;
	max-height: 200px;
	display: inline-block;

	img {
		max-width: 100%;
		height: auto;
		display: block;
	}

	> div {
		position: absolute;
		top: 8px;
		right: 8px;
	}
`;

interface IQuillProps {
	name: string;
	placeholder?: string;
	hasMediaUpload?: boolean;
}

function QuillValue({ name, placeholder, hasMediaUpload }: IQuillProps) {
	const [field, , { setValue }] = useField(name);

	return (
		<QuillInput
			value={field.value}
			onChange={(value) => setValue(value)}
			placeholder={placeholder}
			hasMediaUpload={hasMediaUpload}
		/>
	);
}

function ImageUploadValue({ name, accept, onUpload, uploading }: IImageUploadProps) {
	const { t } = useTranslation();
	const [field, , { setValue }] = useField<{ id: string; url: string } | undefined>(name);
	const [showUpload, setShowUpload] = useState(false);

	const handleFileSelected = async (_fileUrl: unknown, fileData: File) => {
		if (!onUpload) return;

		const result = await onUpload(fileData);
		setValue(result);
		setShowUpload(false);
	};

	const handleRemove = () => {
		setValue(undefined);
	};

	if (field.value?.url) {
		return (
			<ImagePreviewContainer>
				<img src={field.value.url} alt="" />
				<IconButton icon={<CloseIcon />} onClick={handleRemove} />
			</ImagePreviewContainer>
		);
	}

	if (showUpload) {
		if (uploading) {
			return <Spinner centered />;
		}

		return (
			<ImageUpload
				accept={accept ?? ['image/jpeg', 'image/png', 'image/webp']}
				translationPropertyKey="image"
				onFileSelected={handleFileSelected}
				onError={() => setShowUpload(false)}
			/>
		);
	}

	return (
		<Flex>
			<Button
				type="secondary"
				icon={<UploadIcon />}
				title={t('forms.file-upload.image.browse')}
				onClick={() => setShowUpload(true)}
				small
			/>
		</Flex>
	);
}

interface PlainTextProps {
	value?: string;
	name: string;
	onChange?: (textValue?: string) => void;
	alwaysFocused?: boolean;
}

function PlainTextValue({ value, name, onChange, alwaysFocused }: PlainTextProps) {
	const [isFocused, setIsFocused] = useState(false);

	function handleClick() {
		setIsFocused(true);
	}

	function onBlur(event?: React.ChangeEvent<HTMLInputElement>) {
		setIsFocused(false);

		if (event) {
			if (event.target.value !== '') {
				onChange?.(event.target.value);
			} else {
				onChange?.('');
			}
		}
	}

	return (
		<ValueWrapper focused={isFocused || alwaysFocused}>
			<span onClick={handleClick} onKeyUp={handleClick} role="button" tabIndex={0}>
				<Value color="slateBlue">{value || '—'}</Value>
			</span>

			{(isFocused || alwaysFocused) && (
				<InputElement
					autoFocus
					value={value}
					onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
						onChange?.(event.target.value);
					}}
					type={FieldType.Text}
					placeholder="—"
					name={name}
					onBlur={onBlur}
					fullWidth
				/>
			)}
		</ValueWrapper>
	);
}

interface ColorPickerProps {
	value?: string;
	onChange?: (value?: string) => void;
	name: string;
	type: DetailsTableFormValueTypes;
}

function ColorValue({ value, onChange = () => {}, name, type }: ColorPickerProps) {
	const [colorValue, setColorValue] = useState<string | undefined>(value);

	useDebounce(
		() => {
			onChange(colorValue);
		},
		100,
		[colorValue],
	);

	return (
		<>
			<HexColorPicker color={value} onChange={setColorValue} />

			<Box mt={2}>
				<PlainTextValue value={value} onChange={onChange} name={name} />
			</Box>
		</>
	);
}

interface IProps {
	initialValue?: string | number | boolean | Date | null;
	options?: IOptions[];
	fetchContent?: QueryHook;
	createContent?: MutationHook;
	alwaysFocused?: boolean;
	name: string;
	label?: string;
	onEditingForm?: (isEditing: boolean) => void;
	type: DetailsTableFormValueTypes;
	onClick?: () => void | undefined;
	placeholder?: string;
	types?: GroupType[];
	single?: boolean;
	required?: boolean;
	disabled?: boolean;
	// Select props
	search?: boolean;
	// ImageUpload props
	accept?: string[];
	onUpload?: (file: File) => Promise<{ id: string; url: string }>;
	uploading?: boolean;
	// Quill props
	hasMediaUpload?: boolean;
	// Loading state
	loading?: boolean;
}

export default function DetailsTableFormValue({
	initialValue,
	options,
	fetchContent,
	createContent,
	onEditingForm,
	name,
	label,
	type,
	onClick,
	alwaysFocused,
	placeholder,
	types,
	single,
	required,
	disabled,
	search,
	accept,
	onUpload,
	uploading,
	hasMediaUpload,
	loading,
}: IProps): ReactElement {
	const [field, , helpers] = useField(name);

	if (loading) {
		return (
			<Wrapper>
				<Spinner centered />
			</Wrapper>
		);
	}

	return (
		<Wrapper>
			{type === DetailsTableFormValueTypes.Text && (
				<TextValue
					initialValue={field.value}
					name={name}
					label={label}
					type={type}
					alwaysFocused={alwaysFocused}
				/>
			)}

			{type === DetailsTableFormValueTypes.Number && (
				<NumberValue
					initialValue={field.value}
					name={name}
					label={label}
					type={type}
					alwaysFocused={alwaysFocused}
				/>
			)}

			{type === DetailsTableFormValueTypes.TextMultiline && (
				<TextValue initialValue={field.value} name={name} label={label} type={type} />
			)}

			{type === DetailsTableFormValueTypes.Date && <DateValue name={name} />}

			{type === DetailsTableFormValueTypes.Custom && (
				<span onClick={onClick} onKeyUp={onClick} role="button" tabIndex={0}>
					<Value color="slateBlue">{initialValue?.toString()}</Value>
				</span>
			)}

			{type === DetailsTableFormValueTypes.Multilingual && (
				<Input.Localizable name={name} aria-label={label} />
			)}

			{type === DetailsTableFormValueTypes.MultilingualQuill && (
				<QuillInput.Localizable.Formik name={name} />
			)}

			{type === DetailsTableFormValueTypes.Select && (
				<SelectValue options={options} name={name} label={label} search={search} />
			)}

			{type === DetailsTableFormValueTypes.MultiSelect && (
				<MultiSelectValue options={options} name={name} label={label} />
			)}

			{type === DetailsTableFormValueTypes.Switch && (
				<Switch
					aria-label={label}
					on={field.value || initialValue}
					onChange={() => helpers.setValue(!field.value)}
				/>
			)}

			{type === DetailsTableFormValueTypes.ColorPicker && (
				<ColorValue
					value={field.value}
					onChange={(value) => helpers.setValue(value)}
					name={name}
					type={type}
				/>
			)}

			{type === DetailsTableFormValueTypes.CreateSelect && (
				<CreateSelectValue
					fetchContent={fetchContent}
					createContent={createContent}
					name={name}
					placeholder={placeholder}
				/>
			)}

			{type === DetailsTableFormValueTypes.GroupSelector && (
				<GroupSelectorValue
					name={name}
					types={types}
					single={single}
					required={required}
					disabled={disabled}
				/>
			)}

			{type === DetailsTableFormValueTypes.ImageUpload && (
				<ImageUploadValue name={name} accept={accept} onUpload={onUpload} uploading={uploading} />
			)}

			{type === DetailsTableFormValueTypes.Quill && (
				<QuillValue name={name} placeholder={placeholder} hasMediaUpload={hasMediaUpload} />
			)}
		</Wrapper>
	);
}
